import { zodResolver } from '@hookform/resolvers/zod';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Edit,
  MoreHorizontal,
  Plus,
  Trash,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useCreateActMutation } from '~entities/recital-manager/api/create-act';
import { useDeleteActMutation } from '~entities/recital-manager/api/delete-act';
import { useGetAllActs } from '~entities/recital-manager/api/get-all-acts';
import { useUpdateActMutation } from '~entities/recital-manager/api/update-act';
import type { Act } from '~entities/recital-manager/model/recital';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/shadcn/ui/alert-dialog';
import { Badge } from '~/shared/shadcn/ui/badge';
import { Button } from '~/shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/shared/shadcn/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shared/shadcn/ui/form';
import { Input } from '~/shared/shadcn/ui/input';
import { Textarea } from '~/shared/shadcn/ui/textarea';

import { PerformancesList } from './performances-list';

interface ActsListProps {
  showId: string;
  recitalId: string;
}

const actFormSchema = z.object({
  name: z.string().min(1, 'Act name is required'),
  description: z.string().optional(),
  duration: z.coerce.number().optional(),
  notes: z.string().optional(),
});

type ActFormValues = z.infer<typeof actFormSchema>;

export const ActsList = ({ showId, recitalId }: ActsListProps) => {
  const { hasAdminAccess } = useCurrentUserRole();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAct, setSelectedAct] = useState<Act | null>(null);
  const [expandedActIds, setExpandedActIds] = useState<Set<string>>(new Set());

  const { data: actsData, isLoading } = useGetAllActs({ showId });
  const createActMutation = useCreateActMutation();
  const updateActMutation = useUpdateActMutation();
  const deleteActMutation = useDeleteActMutation();

  const form = useForm<ActFormValues>({
    resolver: zodResolver(actFormSchema),
    defaultValues: {
      name: '',
      description: '',
      duration: undefined,
      notes: '',
    },
  });

  const resetForm = () => {
    form.reset({
      name: '',
      description: '',
      duration: undefined,
      notes: '',
    });
  };

  const handleCreate = (values: ActFormValues) => {
    createActMutation.mutate(
      {
        showId,
        ...values,
        orderIndex: (actsData?.length ?? 0) + 1,
      },
      {
        onSuccess: () => {
          toast.success('Act created successfully!');
          setCreateDialogOpen(false);
          resetForm();
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            'Failed to create act';
          toast.error(errorMessage);
        },
      },
    );
  };

  const handleEdit = (values: ActFormValues) => {
    if (!selectedAct) return;

    updateActMutation.mutate(
      {
        showId,
        actId: selectedAct._id,
        ...values,
      },
      {
        onSuccess: () => {
          toast.success('Act updated successfully!');
          setEditDialogOpen(false);
          setSelectedAct(null);
          resetForm();
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            'Failed to update act';
          toast.error(errorMessage);
        },
      },
    );
  };

  const handleDelete = () => {
    if (!selectedAct) return;

    deleteActMutation.mutate(
      { showId, actId: selectedAct._id },
      {
        onSuccess: () => {
          toast.success('Act deleted successfully!');
          setDeleteDialogOpen(false);
          setSelectedAct(null);
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.error?.message ??
            error.response?.data?.message ??
            'Failed to delete act. Make sure all performances are removed first.';
          toast.error(errorMessage);
        },
      },
    );
  };

  const openEditDialog = (act: Act) => {
    setSelectedAct(act);
    form.reset({
      name: act.name,
      description: act.description ?? '',
      duration: act.duration,
      notes: act.notes ?? '',
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (act: Act) => {
    setSelectedAct(act);
    setDeleteDialogOpen(true);
  };

  const toggleActExpansion = (actId: string) => {
    setExpandedActIds((prev) => {
      const next = new Set(prev);
      if (next.has(actId)) {
        next.delete(actId);
      } else {
        next.add(actId);
      }
      return next;
    });
  };

  const acts = actsData ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='min-w-0'>
          <h3 className='text-lg font-semibold'>Acts & Performances</h3>
          <p className='text-sm text-muted-foreground'>
            Organize performances into acts for better show structure
          </p>
        </div>
        {hasAdminAccess && (
          <Button
            size='sm'
            className='w-full shrink-0 sm:w-auto'
            onClick={() => {
              setCreateDialogOpen(true);
            }}
          >
            <Plus />
            Add Act
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className='py-8 text-center text-sm'>Loading acts...</div>
      ) : acts.length === 0 ? (
        <Card>
          <CardContent className='py-12 text-center'>
            <p className='mb-4 text-muted-foreground'>
              No acts created yet. Acts help organize performances into logical
              sections (e.g., Act 1, Act 2, Intermission).
            </p>
            {hasAdminAccess && (
              <Button
                onClick={() => {
                  setCreateDialogOpen(true);
                }}
              >
                <Plus />
                Create First Act
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className='space-y-3'>
          {acts.map((act, index) => {
            const isExpanded = expandedActIds.has(act._id);
            return (
              <Card key={act._id} className='overflow-hidden'>
                <CardHeader className='pb-3'>
                  <div className='flex items-start gap-2 sm:items-center sm:gap-3'>
                    <Button
                      variant='ghost'
                      size='sm'
                      className='size-8 shrink-0 p-0'
                      onClick={() => {
                        toggleActExpansion(act._id);
                      }}
                    >
                      {isExpanded ? (
                        <ChevronDown className='size-4' />
                      ) : (
                        <ChevronRight className='size-4' />
                      )}
                    </Button>

                    <div className='min-w-0 flex-1'>
                      <div className='flex flex-wrap items-center gap-2'>
                        <CardTitle className='text-base capitalize break-words'>
                          Act {index + 1}: {act.name}
                        </CardTitle>
                        {act.duration ? (
                          <Badge variant='outline' className='gap-1 shrink-0'>
                            <Clock className='size-3' />
                            {act.duration} min
                          </Badge>
                        ) : null}
                      </div>
                      {act.description && (
                        <CardDescription className='mt-1'>
                          {act.description}
                        </CardDescription>
                      )}
                      {act.notes && (
                        <p className='mt-1 text-xs text-muted-foreground'>
                          {act.notes}
                        </p>
                      )}
                    </div>

                    {hasAdminAccess ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='-mr-1 shrink-0'
                          >
                            <MoreHorizontal className='size-4' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem
                            onClick={() => {
                              openEditDialog(act);
                            }}
                          >
                            <Edit className='mr-2 size-4' />
                            Edit Act
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              openDeleteDialog(act);
                            }}
                            className='text-red-600'
                          >
                            <Trash className='mr-2 size-4' />
                            Delete Act
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className='pt-0'>
                    <div className='border-t pt-4'>
                      <PerformancesList actId={act._id} recitalId={recitalId} />
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Act Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Act</DialogTitle>
            <DialogDescription>
              Add a new act to organize performances (e.g., &quot;Act 1&quot;,
              &quot;Opening Number&quot;, &quot;Finale&quot;)
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleCreate)}
              className='space-y-4'
            >
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Act Name *</FormLabel>
                    <FormControl>
                      <Input placeholder='Act 1' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='description'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input placeholder='Opening performances' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='duration'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estimated Duration (minutes)</FormLabel>
                    <FormControl>
                      <Input type='number' placeholder='30' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='notes'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder='Any special notes for this act...'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => {
                    setCreateDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type='submit' disabled={createActMutation.isPending}>
                  {createActMutation.isPending ? 'Creating...' : 'Create Act'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Act Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Act</DialogTitle>
            <DialogDescription>Update act details</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleEdit)}
              className='space-y-4'
            >
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Act Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='description'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='duration'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estimated Duration (minutes)</FormLabel>
                    <FormControl>
                      <Input type='number' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='notes'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => {
                    setEditDialogOpen(false);
                    setSelectedAct(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type='submit' disabled={updateActMutation.isPending}>
                  {updateActMutation.isPending ? 'Updating...' : 'Update Act'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Act</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedAct?.name}&quot;?
              All performances in this act must be removed first. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className='bg-red-600 hover:bg-red-700'
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
